import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import StandardModal from 'components/consumer/StandardModal';
import IconReturns from 'components/icons/iconReturns';
import IconMaterialsDesigned from 'components/icons/iconMaterialsDesigned';
import IconCalendar from 'components/icons/iconCalendar';
import IconDeliveryFlat from 'components/icons/iconDeliveryFlat';
import IconClock from 'components/icons/IconClock';
import { intersection } from 'lodash';
import Placeholder from 'components/consumer/Placeholder';
import PDPValuePropsModal from './PDPValuePropsModal';

export const convertPolicyToArray = policy =>
  policy
    .replace(
      'Unlimited furniture delivery for one flat fee',
      'Unlimited flat rate shipping'
    )
    .replace('Free Returns within 14 days of receipt', '14-Day Returns')
    .replace('14-Day Returns', '14-day returns')
    .replace('Free Returns', '90-Day Returns')
    .replace('90-Day Returns', '90-day returns')
    .replace('30-Day Returns', '30-day returns')
    .replace('365-Day Home Trial', '')
    .replace(/- /g, '')
    .split(/\n|\r/)
    .filter(Boolean);

const getValueProps = (
  type,
  fields,
  efgFlag,
  isExternal,
  hideReturns,
  hideUnlimitedShipping,
  campaign
) => {
  const items = [];
  if (fields && fields.description_2) {
    const valueProps = convertPolicyToArray(fields.description_2);
    // ref: https://stitchindustries.atlassian.net/browse/CN-1003
    valueProps.forEach(value => {
      if (value.includes('flat rate')) {
        if (!hideUnlimitedShipping) {
          items.push({
            id: 'FLAT_FEE',
            icon: IconDeliveryFlat,
            value: value.trim(),
            width: 23,
          });
        }
      } else if (value.includes('30-day returns')) {
        if (hideReturns === false) {
          items.push({
            id: 'THIRTY_DAYS_RETURNS',
            icon: IconReturns,
            value: value.trim(),
            width: 26,
          });
        }
      } else if (value.includes('90-day returns')) {
        if (hideReturns === false) {
          items.push({
            id: 'NINETY_DAYS_RETURNS',
            icon: IconReturns,
            value: value.trim(),
            width: 26,
          });
        }
      } else if (value.includes('Difficulty:Limited Lifetime Warranty')) {
        items.push({
          id: 'LIFETIME_DIFFICULTY',
          icon: IconMaterialsDesigned,
          value: 'Limited lifetime warranty',
          width: 20,
        });
      } else if (value.includes('Outdoor:Limited Lifetime Warranty')) {
        items.push({
          id: 'LIFETIME_OUTDOOR',
          icon: IconMaterialsDesigned,
          value: 'Limited warranty',
          width: 20,
        });
      } else if (
        value.includes('CaseGoods:Limited Lifetime Warranty') ||
        value.includes('DiningChairs:Limited Lifetime Warranty')
      ) {
        items.push({
          id: 'LIFETIME_CASE_GOODS',
          icon: IconMaterialsDesigned,
          value: 'Limited warranty',
          width: 20,
        });
      } else if (
        value.toLowerCase().includes('14 days') ||
        value.toLowerCase().includes('14-day')
      ) {
        if (hideReturns === false) {
          // CON-2378: show 90 days for all EFG-Internal
          if (efgFlag) {
            if (!isExternal) {
              if (value.includes('14-day returns')) {
                items.push({
                  id: 'DAY_14_RETURNS',
                  icon: IconReturns,
                  value: '14-day returns',
                  width: 24,
                });
              } else {
                items.push({
                  id: 'NINETY_DAYS_RETURNS',
                  icon: IconReturns,
                  value: '90-day returns',
                  width: 26,
                });
              }
            } else {
              items.push({
                id: 'DAY_14_RETURNS',
                icon: IconReturns,
                value: '14-day returns',
                width: 26,
              });
            }
          } else {
            items.push({
              id: 'DAY_14_RETURNS',
              icon: IconReturns,
              value: value.trim(),
              width: 26,
            });
          }
        }
      } else if (value.includes('Final sale')) {
        items.push({
          id: 'FINAL_SALE',
          icon: IconCalendar,
          value: value.trim(),
          width: 24,
        });
      }
    });
  }

  if (campaign === 'barbie') {
    items.push({
      id: 'LIMITED_EDITION',
      icon: IconClock,
      value: 'Limited Edition',
      width: 26,
    });
  }

  return items;
};

const PDPKeySellingPointsUI = ({
  type,
  fields,
  efg_flag,
  is_external,
  category_ids,
  difficulty,
  showClearanceWarning,
  loading,
  campaign,
}) => {
  const [valueProps, setValueProps] = useState(null);
  const [activePanel, setActivePanel] = useState(null);

  const shouldHideUnlimitedShippingByCategoryId = categoryIds =>
    // hide unlimited shipping for decoration-accessories category, categoryId:222
    categoryIds && categoryIds.includes(222);
  const shouldHideShippingAndReturnsForFabric = productType =>
    productType && productType === 'Fabric';

  useEffect(() => {
    if (!loading) {
      let hideUnlimitedShipping = category_ids
        ? shouldHideUnlimitedShippingByCategoryId(category_ids)
        : false;

      const hideShippingAndReturnsForFabric = type
        ? shouldHideShippingAndReturnsForFabric(type.name)
        : false;

      hideUnlimitedShipping =
        hideShippingAndReturnsForFabric === true ? true : hideUnlimitedShipping;
      const hideReturns =
        hideShippingAndReturnsForFabric === true
          ? true
          : !!showClearanceWarning;

      const applyProductWarrantyPolicy = theFields => {
        const theseFields = { ...theFields };
        const difficultyList = ['A', 'B', 'C', 'D', 'E', 'F', 'CUBE'];
        const matchDiningChairCategories = intersection([247], category_ids);
        const matchOutdoorCategories = intersection(
          [624, 625, 626, 627, 628, 629],
          category_ids
        );
        const ignoreDecorCategory = intersection([222], category_ids);

        if (efg_flag && is_external) {
          return theseFields;
        }

        if (
          Array.isArray(ignoreDecorCategory) &&
          ignoreDecorCategory.length > 0
        ) {
          if (
            theseFields.description_2 &&
            /- Free Returns/.test(theseFields.description_2)
          ) {
            theseFields.description_2 = theseFields.description_2.replace(
              /- Free Returns/,
              'Free Returns within 14 days of receipt'
            );
          }
          return theseFields;
        }

        if (theseFields.description_2) {
          if (difficultyList.indexOf(difficulty) > -1) {
            theseFields.description_2 = theseFields.description_2.replace(
              'Limited Lifetime Warranty',
              'Difficulty:Limited Lifetime Warranty'
            );
          } else if (
            Array.isArray(matchDiningChairCategories) &&
            matchDiningChairCategories.length > 0 &&
            Array.isArray(matchOutdoorCategories) &&
            matchOutdoorCategories.length > 0
          ) {
            theseFields.description_2 = `${theseFields.description_2.trim()}\n - Outdoor:Limited Lifetime Warranty`;
          } else if (
            Array.isArray(matchDiningChairCategories) &&
            matchDiningChairCategories.length > 0
          ) {
            theseFields.description_2 = `${theseFields.description_2.trim()}\n - DiningChairs:Limited Lifetime Warranty`;
          } else if (
            Array.isArray(matchOutdoorCategories) &&
            matchOutdoorCategories.length > 0
          ) {
            theseFields.description_2 = `${theseFields.description_2.trim()}\n - Outdoor:Limited Lifetime Warranty`;
          } else {
            theseFields.description_2 = `${theseFields.description_2.trim()}\n - CaseGoods:Limited Lifetime Warranty`;
          }
        }
        return theseFields;
      };

      const anotherValueProps = getValueProps(
        type,
        applyProductWarrantyPolicy(fields),
        efg_flag,
        is_external,
        hideReturns,
        hideUnlimitedShipping,
        campaign
      );
      setValueProps(anotherValueProps);
    }
  }, [
    type,
    fields,
    loading,
    efg_flag,
    is_external,
    category_ids,
    showClearanceWarning,
    difficulty,
    campaign,
  ]);

  if (loading) {
    return (
      <div className="flex flex-col justify-center mt-8 p-0 w-full">
        <ul className="items-center flex justify-center list-none m-0 p-0">
          {[0, 1, 2].map(idx => (
            <li
              className="items-center flex flex-col justify-center list-none m-0 p-0"
              /* 
              disabling this particular no-array-index-key error because
              we are not mapping an object but rather a hardcoded array of
              unique numbers that will not change
              */
              // eslint-disable-next-line react/no-array-index-key
              key={`PDPKeySellingPointsUI__item${idx}`}
            >
              <div className="text-xs bg-transparent border-2 border-solid border-transparent cursor-pointer text-gray-light1 items-center flex flex-col px-2 py-0.5 m-0 text-center max-w-[33vw] !normal-case max-[767px]:flex-1 max-[767px]:leading-[unset] max-[767px]:p-0 max-[767px]:m-0 ml-5 last:mr-5 focus:text-brand hover:text-brand [&_span]:max-[767px]:max-w-[90px]">
                <div className="flex items-center justify-center mb-2">
                  <Placeholder
                    type="round"
                    loading
                    style={{ height: '36px', width: '36px' }}
                  />
                </div>
                <span className="max-[767px]:max-w-[90px]">
                  <Placeholder
                    type="rect"
                    loading
                    style={{ height: '12px', width: '120px' }}
                  />
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center mt-8 p-0 w-full">
      <ul className="items-center flex justify-center list-none m-0 p-0">
        {valueProps?.map(item => (
          <li
            className="items-center flex flex-col justify-center list-none m-0 p-0"
            key={`li${item.value}${item.id}`}
          >
            <button
              type="button"
              key={item.value + item.id}
              className="text-xs bg-transparent border-2 border-solid border-transparent cursor-pointer text-gray-light1 items-center flex flex-col px-2 py-0.5 m-0 text-center max-w-[33vw] !normal-case max-[767px]:flex-1 max-[767px]:leading-[unset] max-[767px]:p-0 max-[767px]:m-0 ml-5 last:mr-5 focus:text-brand hover:text-brand"
              onClick={() => {
                switch (item.id) {
                  case 'FLAT_FEE':
                    setActivePanel({
                      id: item.id,
                      title: 'Contactless, flat-rate delivery',
                      icon: item.icon,
                    });
                    break;
                  case 'THIRTY_DAYS_RETURNS':
                    setActivePanel({
                      id: item.id,
                      title: '30-day returns',
                      icon: item.icon,
                    });
                    break;
                  case 'NINETY_DAYS_RETURNS':
                    setActivePanel({
                      id: item.id,
                      title: '90-day returns',
                      icon: item.icon,
                    });
                    break;
                  case 'DAY_14_RETURNS':
                    setActivePanel({
                      id: item.id,
                      title: '14-day returns',
                      icon: item.icon,
                    });
                    break;
                  case 'FINAL_SALE':
                    setActivePanel({
                      id: item.id,
                      title: 'Final Sale',
                      icon: item.icon,
                    });
                    break;
                  case 'LIFETIME_CASE_GOODS':
                  case 'LIFETIME_OUTDOOR':
                  case 'LIFETIME_DINING_CHAIR':
                    setActivePanel({
                      id: item.id,
                      title: 'Limited Warranty',
                      icon: item.icon,
                    });
                    break;
                  case 'LIFETIME_DIFFICULTY':
                    setActivePanel({
                      id: item.id,
                      title: 'Limited lifetime warranty',
                      icon: item.icon,
                    });
                    break;
                  case 'LIMITED_EDITION':
                    setActivePanel({
                      id: item.id,
                      title: 'Limited Edition',
                      icon: item.icon,
                    });
                    break;
                  default:
                    break;
                }
              }}
            >
              <div className="flex items-center justify-center mb-2">
                <item.icon
                  aria-hidden="true"
                  color="currentColor"
                  height={26}
                  width={item.width}
                />
              </div>

              <span className="max-[767px]:max-w-[90px]">
                {item.value}
                <sup>*</sup>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {activePanel && (
        <StandardModal
          isOpen
          title={activePanel?.title}
          icon={
            <div className="[&_g]:stroke-[#fff]">
              <activePanel.icon width={25} height={30} color="#ffffff" />
            </div>
          }
          onClose={() => {
            setActivePanel(null);
          }}
        >
          <PDPValuePropsModal itemId={activePanel.id} />
        </StandardModal>
      )}
    </div>
  );
};

PDPKeySellingPointsUI.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.shape({
    name: PropTypes.string,
  }),
  efg_flag: PropTypes.number,
  difficulty: PropTypes.string,
  is_external: PropTypes.number,
  category_ids: PropTypes.arrayOf(PropTypes.number),
  fields: PropTypes.shape({
    anchor_link: PropTypes.string,
    background: PropTypes.string,
    block_padding: PropTypes.string,
    description_1: PropTypes.string,
    description_2: PropTypes.string,
    title_1: PropTypes.string,
    title_2: PropTypes.string,
  }),
  showClearanceWarning: PropTypes.bool,
  campaign: PropTypes.string,
};

export default PDPKeySellingPointsUI;
